// ── Processing view ────────────────────────────────────────────────────────────
// Renders the wait screen while the AI try-on is running. When a preview image
// is present (guest/photo flow), show the user's own preview with a Sci-Fi
// analytical scan sweep (canvas Sobel edge trace + cyan wake behind a HUD laser
// line) instead of a text screen. Without a preview (avatars/combo flow), fall
// back to the spinner + progress card. Both stay normal white cards so the
// phase transition morphs smoothly.

import { esc } from '../helpers';
import type { ViewContext } from '../modal-types';
import { startSweepScan } from '../glitch-sweep';

export function renderProcessing(ctx: ViewContext): void {
  const { state, content } = ctx;

  if (state.preview) {
    // The preview <img> is the same cached source the user already saw in the
    // upload/avatar preview — it sizes the scan box to the image's natural ratio
    // (so the modal doesn't shrink) and shows instantly while the canvas sweep
    // loads and draws over it. The canvas is absolutely overlaid via CSS.
    content.innerHTML = `
      ${ctx.headerHTML()}
      <div class="ts-mbody" style="padding:var(--ts-modal-pad)">
        <div class="ts-glitch" id="ts-glitch">
          <img src="${esc(state.preview)}" class="ts-scan-img" alt="" />
          <canvas id="ts-sweep" aria-hidden="true"></canvas>
        </div>
      </div>
    `;

    // Same source as the preview → already cached, so show it immediately.
    const scanImg = content.querySelector<HTMLImageElement>('.ts-scan-img');
    if (scanImg) {
      if (scanImg.complete && scanImg.naturalWidth > 0) scanImg.classList.add('ts-img-loaded');
      else scanImg.addEventListener('load', () => scanImg.classList.add('ts-img-loaded'));
    }

    const canvas = content.querySelector<HTMLCanvasElement>('#ts-sweep');
    if (canvas) {
      startSweepScan(canvas, state.preview, () => ctx.reflow());
    }
    return;
  }

  const hasProgress = state.progress > 0;

  content.innerHTML = `
    ${ctx.headerHTML()}
    <div class="ts-mbody" style="text-align:center;padding:var(--ts-modal-pad)">
      <div class="ts-spinner"></div>
      <p class="ts-proc-title">${esc(state.processingLabel)}</p>
      ${hasProgress ? `
        <div class="ts-proc-bar-wrap">
          <div class="ts-proc-bar" style="width:${state.progress}%"></div>
        </div>
        <p class="ts-proc-pct">${state.progress}%</p>
      ` : ''}
      <p class="ts-proc-sub">This can take a few seconds</p>
    </div>
  `;
}